import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './events.css'; 

export default function EventRecap() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const eventDocRef = doc(db, 'events', eventId);
        const eventDocSnap = await getDoc(eventDocRef);
        
        if (eventDocSnap.exists()) {
          setEvent({ ...eventDocSnap.data(), dbId: eventDocSnap.id });
        } else {
          console.error("No such event!");
        }

        const reviewsRef = collection(db, 'event_reviews');
        const q = query(reviewsRef, where('eventId', '==', eventId), where('status', '==', 'approved'));
        const reviewsSnap = await getDocs(q);
        
        const fetchedReviews = reviewsSnap.docs.map(d => ({ ...d.data(), dbId: d.id }));
        setReviews(fetchedReviews);
        
      } catch (error) {
        console.error("Error fetching recap data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventId]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8F9FA' }}>
        <div className="spinner" style={{ width: '50px', height: '50px', border: '5px solid rgba(0,48,73,0.1)', borderTopColor: '#003049', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8F9FA' }}>
        <p style={{ color: '#F07167', fontSize: '20px', fontWeight: 'bold' }}>Event not found.</p>
        <button className="btn-explore" onClick={() => navigate('/events')} style={{ marginTop: '20px', padding: '10px 24px', borderRadius: '30px', backgroundColor: '#003049', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: '600' }}>Go Back</button>
      </div>
    );
  }

  const topicsList = event.topics ? event.topics.split(',').map(t => t.trim()).filter(t => t) : [];

  // Combine legacy recapImg with new recapMediaUrls array
  const mediaUrls = event.recapMediaUrls || [];
  if (event.recapImg && !mediaUrls.includes(event.recapImg)) {
     mediaUrls.unshift(event.recapImg);
  }

  const isVideoOrAudio = (url) => {
      const cleanUrl = url.split('?')[0];
      return cleanUrl.match(/\.(mp4|webm|ogg|mp3)$/i);
  };

  return (
    <div className="events-recap-page" style={{ minHeight: '100vh', backgroundColor: '#FAFAFB', fontFamily: '"Inter", sans-serif' }}>
      
      {/* HERO SECTION */}
      <div style={{ position: 'relative', overflow: 'hidden', backgroundColor: '#003049', color: '#fff', padding: '120px 20px 60px', textAlign: 'center' }}>
        {/* Abstract Background Shapes */}
        <div style={{ position: 'absolute', top: '-50%', left: '-10%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(249,155,0,0.2) 0%, transparent 70%)', borderRadius: '50%', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '-20%', right: '-5%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(240,113,103,0.2) 0%, transparent 70%)', borderRadius: '50%', zIndex: 0 }} />
        
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>
          <button 
            onClick={() => navigate('/events')} 
            style={{ position: 'absolute', top: '-60px', left: '0', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', padding: '8px 16px', borderRadius: '30px', transition: 'all 0.3s' }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <span>←</span> Back to Events
          </button>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {topicsList.map((t, i) => (
              <span key={i} style={{ backgroundColor: 'rgba(249,155,0,0.15)', border: '1px solid rgba(249,155,0,0.4)', color: '#F99B00', padding: '6px 14px', borderRadius: '30px', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>{t}</span>
            ))}
          </div>
          <h1 style={{ fontSize: '56px', fontWeight: '800', marginBottom: '20px', lineHeight: '1.2', letterSpacing: '-1px' }}>{event.title}</h1>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', fontSize: '16px', color: 'rgba(255,255,255,0.8)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><i style={{ fontStyle: 'normal', fontSize: '20px' }}>📅</i> {event.date}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><i style={{ fontStyle: 'normal', fontSize: '20px' }}>📍</i> {event.location}</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* CAROUSEL SECTION */}
        {mediaUrls.length > 0 && (
          <div style={{ marginTop: '-40px', position: 'relative', zIndex: 10, paddingBottom: '60px' }}>
            <Swiper
              effect={'coverflow'}
              grabCursor={true}
              centeredSlides={true}
              slidesPerView={'auto'}
              initialSlide={Math.floor(mediaUrls.length / 2)}
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 150,
                modifier: 2.5,
                slideShadows: true,
              }}
              pagination={{ clickable: true, dynamicBullets: true }}
              navigation={true}
              modules={[EffectCoverflow, Pagination, Navigation, Autoplay]}
              className="recap-swiper"
              style={{ width: '100%', padding: '40px 0 60px' }}
            >
              {mediaUrls.map((url, idx) => (
                <SwiperSlide key={idx} style={{ width: '700px', maxWidth: '85vw', height: '450px', borderRadius: '24px', overflow: 'hidden', backgroundColor: '#000', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {isVideoOrAudio(url) ? (
                    <video src={url} controls loop style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <img src={url} alt={`Recap ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </SwiperSlide>
              ))}
            </Swiper>

            <style>{`
              .recap-swiper .swiper-pagination-bullet { background: #003049; opacity: 0.5; }
              .recap-swiper .swiper-pagination-bullet-active { background: #F99B00; opacity: 1; }
              .recap-swiper .swiper-button-next, .recap-swiper .swiper-button-prev { color: #fff; background: rgba(0,0,0,0.3); width: 50px; height: 50px; border-radius: 50%; backdrop-filter: blur(5px); }
              .recap-swiper .swiper-button-next:after, .recap-swiper .swiper-button-prev:after { font-size: 20px; font-weight: bold; }
              .recap-swiper .swiper-button-next:hover, .recap-swiper .swiper-button-prev:hover { background: rgba(249,155,0,0.8); }
            `}</style>
          </div>
        )}

        {/* SECTION-WISE CONTENT */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', paddingBottom: '80px', marginTop: mediaUrls.length > 0 ? '0' : '60px' }}>
          
          {/* EVENT SUMMARY */}
          {event.recapText && (
            <div style={{ backgroundColor: '#fff', borderRadius: '30px', padding: '40px', boxShadow: '0 10px 40px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', backgroundColor: 'rgba(240,113,103,0.1)', color: '#F07167', padding: '8px 16px', borderRadius: '20px', fontWeight: 'bold', fontSize: '14px', marginBottom: '24px' }}>
                <span style={{ fontSize: '18px' }}>📝</span> Summary
              </div>
              <p style={{ color: '#444', fontSize: '17px', lineHeight: '1.8', whiteSpace: 'pre-line' }}>{event.recapText}</p>
            </div>
          )}

          {/* REVIEWS SECTION */}
          {reviews.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', backgroundColor: 'rgba(249,155,0,0.1)', color: '#F99B00', padding: '8px 16px', borderRadius: '20px', fontWeight: 'bold', fontSize: '14px', alignSelf: 'flex-start' }}>
                <span style={{ fontSize: '18px' }}>💬</span> Attendee Reviews
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {reviews.map(rev => {
                  const rating = Number(rev.rating) || 5;
                  const emoji = rating >= 5 ? '🤩' : rating >= 4 ? '😊' : rating >= 3 ? '😐' : rating >= 2 ? '😕' : '😞';
                  return (
                    <div key={rev.dbId} style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '20px', boxShadow: '0 5px 20px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.02)', transition: 'transform 0.2s' }}
                         onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
                         onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#003049', color: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                            {rev.userName.charAt(0).toUpperCase()}
                          </div>
                          <strong style={{ color: '#003049', fontSize: '16px' }}>{rev.userName}</strong>
                        </div>
                        <span style={{ fontSize: '24px' }}>{emoji}</span>
                      </div>
                      <p style={{ color: '#666', fontSize: '15px', lineHeight: '1.6', margin: 0 }}>"{rev.reviewText}"</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {mediaUrls.length === 0 && !event.recapText && reviews.length === 0 && (
          <div style={{ textAlign: 'center', padding: '100px 20px', color: '#888' }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>📭</div>
            <p style={{ fontSize: '20px', fontWeight: '500' }}>No recap information available for this event yet.</p>
          </div>
        )}

      </div>
    </div>
  );
}
